import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { projectConfigState } from 'recoil/Academy/projectConfigState';
import { useCurrentUser } from 'src/app.hooks/useCurrentUser';
import { useExamLikeHandler } from 'src/app.hooks/useExamLikeHandler';
import { convertExamDetailQueryString } from 'app.feature/academy/cbt.detail/module/convertExamDetailQueryString';
import { TExamsInfo } from 'app.type/examsInfo';
import { TExamsDetails } from 'app.type/examsDetails';
import ExamApi from 'lib/api/exam';
import ShareIconSvg from 'public/icons/share-icon.svg';
import FrameIconSvg from 'public/icons/frame-icon.svg';
import FavoriteIconSvg from 'app.feature/academy/component/icon/heart-icon-regular.svg';
import FavoriteIconSolidSvg from 'app.feature/academy/component/icon/heart-icon-solid.svg';

const CbtDetailTabBar = () => {
  const router = useRouter();
  const examIdx = Number(router.query.examIdx);
  const { user } = useCurrentUser();
  const { handleLoginRedirect, handleLikeButtonClick } = useRecoilValue(projectConfigState);

  const examInfoQueryKey = [`/apiV2/exams/${examIdx}`];

  const {
    data: examInfo,
    isError,
    isLoading,
  } = useQuery<TExamsInfo>({
    queryKey: examInfoQueryKey,
    queryFn: () => ExamApi.getExamInfo(examIdx),
  });

  const { data: examDetail = [] } = useQuery<TExamsDetails[]>({
    queryKey: [
      `/apiV2/exams/${examIdx}/exam-details`,
      convertExamDetailQueryString({ sorts: router.query.sorts }),
    ],
    queryFn: () =>
      ExamApi.getExamDetails(examIdx, convertExamDetailQueryString({ sorts: router.query.sorts })),
  });

  const { handleLikeExam } = useExamLikeHandler(
    examInfoQueryKey,
    (oldData, viewerHasLiked, examIdx) => {
      return {
        ...oldData,
        likeCount: (oldData?.likeCount || 0) + (viewerHasLiked ? -1 : 1),
        viewerHasLiked: !viewerHasLiked,
      };
    }
  );

  const recentSolve = (examDetail || [])
    .map((detail: any) => {
      if ((detail?.recentExamDetailSolves || []).length > 0)
        return detail?.recentExamDetailSolves.at(0);
    })
    .filter((item: any) => item);

  const handleSolveContinue = async () => {
    //로그인 하지 않은 상태
    if (!user) {
      router.push(handleLoginRedirect(window.location.href));
      return;
    }

    router.push(`/api/exam/${examIdx}/contains`);
  };

  const handleShareButtonClick = () => {
    const title = `[${examInfo?.institution}] ${examInfo?.examName}`;
    const url = window.location.href;

    if (typeof navigator.share === 'undefined') {
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(`${url}`)
          .then(() => {
            alert('공유 링크가 클립보드에 복사되었습니다.');
          })
          .catch(() => {
            alert('클립보드 복사에 실패했습니다. 다른 방법으로 공유해주세요.');
          });
      } else {
        alert('공유 기능을 지원하지 않는 브라우저입니다.');
      }
      return;
    }

    navigator
      .share({
        url: url,
        title: title,
      })
      .then(() => {
        console.log('공유가 완료되었습니다.');
      })
      .catch((err) => {
        console.error('공유 중 오류가 발생했습니다.', err);
      });
  };

  const onLikeButtonClick = async (examIdx: number, viewerHasLiked: boolean) => {
    if (!user) {
      router.push(handleLoginRedirect(window.location.href));
      return;
    }

    await handleLikeExam(examIdx, viewerHasLiked);

    if (handleLikeButtonClick) {
      handleLikeButtonClick(examIdx);
    }
  };

  const handleGoToList = () => {
    router.push('/');
  };

  if (isLoading || isError) return null;

  return (
    <StyledWrapper>
      <button onClick={handleGoToList} className="tab-bar-menu">
        <FrameIconSvg />
        <span>CBT</span>
      </button>
      <button onClick={handleShareButtonClick} className="button-share">
        <ShareIconSvg />
        <span>공유</span>
      </button>
      <button
        onClick={() => onLikeButtonClick(examIdx, examInfo.viewerHasLiked)}
        className="button-like"
      >
        <span className="like-icon">
          {examInfo?.viewerHasLiked ? (
            <FavoriteIconSolidSvg className="solid" />
          ) : (
            <FavoriteIconSvg />
          )}{' '}
          <small>{Number(examInfo.likeCount).toLocaleString()}</small>
        </span>
        <span>
          나의 관심시험 추가
          <br />
        </span>
      </button>
      <button onClick={handleSolveContinue} className="button-solve">
        {(recentSolve || []).length > 0 ? '이어서 풀기' : '최신 회차 풀기'}
      </button>
    </StyledWrapper>
  );
};

export default CbtDetailTabBar;

const StyledWrapper = styled.div`
  position: fixed;
  bottom: 0px;
  left: 0;
  z-index: 1000;
  width: 100%;
  height: 54px;
  background-color: #fff;
  border-top: 1px solid #eeeeee;
  display: flex;
  button {
    background-color: #003056;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 10px 0;
    span {
      color: #b9c3ca;
      font-size: 14px;
      font-weight: 400;
    }
    > svg:not(.solid) {
      path {
        fill: #b9c3ca;
      }
    }
  }
  .button-share {
    flex: 1;
  }
  .button-like {
    flex: 2;
    .like-icon {
      display: flex;
      align-items: center;
      gap: 5px;
      color: #b9c3ca;
      font-size: 14px;
      font-weight: 400;
    }
  }
  .button-solve {
    flex: 1.7;
    background-color: #01a0ff;
    color: white;
    font-size: 15px;
    font-weight: 700;
  }

  .tab-bar-menu {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
